import { useState } from "react";
import {
    Container,
    Box,
    Typography,
    TextField,
    LinearProgress,
    useTheme,
    Chip,
    Link,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { FiSearch, FiExternalLink } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const Transactions = ({ data = [] }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");
    const [pageSize, setPageSize] = useState(10);

    const getStatusColor = (status) => {
        const statusColors = {
            success: "success",
            fail: "error",
            pending: "warning",
        };
        return statusColors[status] || "default";
    };

    const getTypeColor = (type) => {
        const typeColors = {
            deposit: "success",
            order_payment: "primary",
        };
        return typeColors[type] || "default";
    };

    const getTypeLabel = (type) => {
        const typeLabels = {
            deposit: "Add Balance",
            order_payment: "Pay for Service",
        };
        return typeLabels[type] || type;
    };

    const handleOrderClick = (orderUuid) => {
        if (orderUuid) {
            navigate(`/Orders/${orderUuid}`);
        }
    };

    // Safe date formatter function
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";

        try {
            // Parse the ISO date string
            const date = new Date(dateString);

            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.warn('Invalid date:', dateString);
                return "Invalid Date";
            }

            // Format with time for transactions
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return "Invalid Date";
        }
    };

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "ID",
            width: 80,
        },
        {
            field: "type",
            headerName: "Type",
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={getTypeLabel(params.row.type)}
                    color={getTypeColor(params.row.type)}
                    size="small"
                />
            ),
        },
        {
            field: "transaction_id",
            headerName: "Transaction ID",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Box sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                    {params.row.transaction_id}
                </Box>
            ),
        },
        {
            field: "gateway",
            headerName: "Gateway",
            width: 120,
            renderCell: (params) => (
                <Chip
                    label={params.row.gateway}
                    color="secondary"
                    variant="outlined"
                    size="small"
                />
            ),
        },
        {
            field: "amount",
            headerName: "Amount",
            width: 120,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 'bold',
                        color: params.row.type === 'deposit' ? 'success.main' : 'primary.main'
                    }}
                >
                    {params.row.currency} {params.row.amount}
                    {params.row.type === 'order_payment' && params.row.order_price && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            (Order: {params.row.currency} {params.row.order_price})
                        </Typography>
                    )}
                </Typography>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.row.status}
                    color={getStatusColor(params.row.status)}
                    size="small"
                />
            ),
        },
        {
            field: "order_uuid",
            headerName: "Order",
            width: 150,
            renderCell: (params) =>
                params.row.order_uuid ? (
                    <Link
                        component="button"
                        onClick={() => handleOrderClick(params.row.order_uuid)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            textDecoration: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        <FiExternalLink size={14} />
                        <Typography variant="body2">
                            View Order
                        </Typography>
                    </Link>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        -
                    </Typography>
                ),
        },
        {
            field: "balance_before",
            headerName: "Balance Before",
            width: 130,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.currency} {params.row.balance_before || '0.00'}
                </Typography>
            ),
        },
        {
            field: "balance_after",
            headerName: "Balance After",
            width: 130,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.currency} {params.row.balance_after || '0.00'}
                </Typography>
            ),
        },
        {
            field: "paid_at",
            headerName: "Paid At",
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {formatDate(params.row.paid_at)}
                </Typography>
            ),
        },
        {
            field: "created_at",
            headerName: "Created",
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {formatDate(params.row.created_at)}
                </Typography>
            ),
        },
    ];

    const filteredRows = data.filter((row) =>
        Object.values(row).some((value) =>
            String(value).toLowerCase().includes(searchText.toLowerCase())
        )
    );

    // Calculate totals
    const depositTotal = data
        .filter(t => t.type === 'deposit' && t.status === 'success')
        .reduce((sum, t) => sum + parseFloat(t.amount), 0);

    const orderPaymentTotal = data
        .filter(t => t.type === 'order_payment' && t.status === 'success')
        .reduce((sum, t) => sum + parseFloat(t.amount), 0);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box
                sx={{
                    mb: 4,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
                        User Transactions ({data.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            Total Deposits: <strong style={{ color: theme.palette.success.main }}>${depositTotal.toFixed(2)}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total Order Payments: <strong style={{ color: theme.palette.primary.main }}>${orderPaymentTotal.toFixed(2)}</strong>
                        </Typography>
                    </Box>
                </Box>

                <TextField
                    variant="outlined"
                    placeholder="Search transactions..."
                    InputProps={{
                        startAdornment: <FiSearch style={{ marginRight: 8 }} />,
                    }}
                    sx={{
                        width: 300,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                        },
                    }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
            </Box>

            <Box
                sx={{
                    height: 600,
                    width: "100%",
                    "& .MuiDataGrid-root": {
                        border: "none",
                        borderRadius: 2,
                        boxShadow: theme.shadows[2],
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: theme.palette.background.paper,
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: theme.palette.background.default,
                    },
                }}
            >
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[10, 25, 50]}
                    pagination
                    autoHeight={false}
                    disableSelectionOnClick
                    loading={false}
                    getRowId={(row) => row.id}
                    components={{
                        LoadingOverlay: LinearProgress,
                    }}
                    sx={{
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                />
            </Box>
        </Container>
    );
};

export default Transactions;